import { useCallback, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Stack } from "@mantine/core";
import { caseImagesService } from "../../services/caseImages.service";
import { internalRenderService } from "../../services/internalRender.service";
import type { PositionedReportFile } from "../../utils/reportComposition";
import type {
  ReportFooterProps,
  ReportHeaderSectionProps,
} from "./ReportAttachmentPage";
import { ReportFilePage, isRenderableReportFile } from "./ReportFilePage";

interface Props {
  caseId: string;
  file: PositionedReportFile;
  internal: boolean;
  firstPageNumber: number;
  totalPages: number;
  footer: Omit<ReportFooterProps, "pageNumber" | "totalPages">;
  headerSectionProps?: ReportHeaderSectionProps;
  onSettled: (id: string, count: number) => void;
  onPending: (id: string) => void;
  onError: () => void;
}

export function ReportCompositionFile(props: Props) {
  const {
    file,
    internal,
    onSettled,
    onPending,
    onError,
    caseId,
    footer,
    firstPageNumber,
    totalPages,
    headerSectionProps,
  } = props;
  const queryScope = useId();
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const requestRef = useRef(0);
  const pendingRef = useRef(false);
  const handleError = useCallback(() => {
    setFailed(true);
    onPending(`file:${file.id}`);
    onError();
  }, [file.id, onError, onPending]);
  const loadUrl = useCallback(
    async ({ signal }: { signal: AbortSignal }) => {
      const request = requestRef.current + 1;
      requestRef.current = request;
      pendingRef.current = true;
      onPending(`file:${file.id}`);
      try {
        if (!file.storageKey)
          throw new Error("Report document locator missing.");
        const result = await (internal
          ? internalRenderService.getSelectedFileUrl(file.id)
          : caseImagesService.getDownloadUrl(file.storageKey, file.name));
        if (!signal.aborted && request === requestRef.current) {
          pendingRef.current = false;
          setFailed(false);
          if (!isRenderableReportFile(file)) onSettled(`file:${file.id}`, 1);
        }
        return { ...result, request };
      } catch (error) {
        if (!signal.aborted && request === requestRef.current) handleError();
        throw error;
      }
    },
    [file, handleError, internal, onPending, onSettled]
  );
  const query = useQuery({
    queryKey: [
      "composition-file",
      queryScope,
      caseId,
      file.id,
      file.storageKey,
      internal,
      attempt,
    ],
    queryFn: loadUrl,
    retry: false,
  });
  const handleReady = useCallback(
    (_id: string, count = 1) => {
      if (pendingRef.current || query.data?.request !== requestRef.current)
        return;
      setFailed(false);
      onSettled(`file:${file.id}`, count);
    },
    [file.id, onSettled, query.data?.request]
  );
  const handleRenderError = useCallback(() => {
    if (!pendingRef.current && query.data?.request === requestRef.current)
      handleError();
  }, [handleError, query.data?.request]);
  const handleRetry = useCallback(() => {
    onPending(`file:${file.id}`);
    setFailed(false);
    setAttempt((value) => value + 1);
  }, [file.id, onPending]);

  if (query.isError)
    return (
      <Alert color="red" title="Report document unavailable">
        <Stack gap="xs">
          {file.name}
          <Button onClick={handleRetry}>Retry document</Button>
        </Stack>
      </Alert>
    );
  return (
    <ReportFilePage
      key={`${attempt}:${query.dataUpdatedAt}:${query.data?.request}`}
      file={file}
      url={query.data?.url}
      failed={failed}
      onLoadComplete={handleReady}
      onLoadError={handleRenderError}
      onRetry={handleRetry}
      allowExternalLink={false}
      headerSectionProps={
        file.kind === "case_file" ? headerSectionProps : undefined
      }
      footerProps={footer}
      firstPageNumber={firstPageNumber}
      totalPages={totalPages}
    />
  );
}
